import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ProductTranslationCompleteness } from '@today/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { type ProductDTO, productService } from '@/services/product.service'
import { actionEnabled, lifecycleAction } from './product-lifecycle'
import { productErrorCode, productErrorMessage } from './product-ui'
import { productKey } from './useProductEditor'
import { translationsKey } from './useProductTranslations'

/** The transition call: success writes the product; a stale refusal reloads what it contradicts. */
function useTransition(product: ProductDTO, onDone: () => void, onError: (error: unknown) => void) {
	const client = useQueryClient()
	const next = lifecycleAction(product.status)
	return useMutation({
		mutationFn: () => productService.transition(product.productId, next?.targetStatus ?? product.status),
		onSuccess: (result) => {
			client.setQueryData(productKey(product.productId), result)
			onDone()
		},
		onError: (error) => {
			onError(error)
			const code = productErrorCode(error)
			if (code === 'PRODUCT_TRANSLATIONS_MISSING') {
				void client.invalidateQueries({ queryKey: translationsKey(product.productId) })
			}
			if (code === 'PRODUCT_TRANSITION_NOT_ALLOWED') {
				void client.invalidateQueries({ queryKey: productKey(product.productId) })
			}
		},
	})
}

/** Delete the product, then return to the list. */
function useDeleteProduct(productId: string, onError: (error: unknown) => void) {
	const navigate = useNavigate()
	return useMutation({
		mutationFn: () => productService.remove(productId),
		onSuccess: () => void navigate('/products'),
		onError,
	})
}

/**
 * Header of the product editor (TO-416 product-admin-ui REQ-007): completeness,
 * the one lifecycle action for the status (publish/reactivate gated on
 * `publishable`, retire confirmed), and delete.
 */
export function useProductLifecycle(
	product: ProductDTO,
	completeness: ProductTranslationCompleteness | undefined,
	caps: { write: boolean; delete: boolean },
) {
	const { t, i18n } = useTranslation('products')
	const [dialog, setDialog] = useState<'retire' | 'delete' | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [dialogError, setDialogError] = useState<string | null>(null)
	const next = lifecycleAction(product.status)
	const message = (e: unknown) => productErrorMessage(e, t, (key) => i18n.exists(key, { ns: 'products' }))
	const transition = useTransition(
		product,
		() => setDialog(null),
		(e) => {
			setDialog(null)
			setError(message(e))
		},
	)
	const remove = useDeleteProduct(product.productId, (e) => setDialogError(message(e)))
	const openDelete = () => {
		setDialogError(null)
		setDialog('delete')
	}
	const enabled = actionEnabled(next?.action ?? 'retire', completeness?.publishable ?? false)

	return {
		t,
		code: product.code,
		name: product.name,
		status: product.status,
		missingLocales: completeness?.missingLocales ?? [],
		action: caps.write && next ? { id: next.action, enabled } : null,
		dialog,
		isBusy: transition.isPending || remove.isPending,
		error,
		dialogError,
		onAction: () => {
			// The view disables the button too; the handler holds the publish gate on its own.
			if (!caps.write || !next || !enabled) return
			setError(null)
			if (next?.action === 'retire') setDialog('retire')
			else transition.mutate()
		},
		onConfirm: () => (dialog === 'retire' ? transition.mutate() : remove.mutate()),
		onCancel: () => setDialog(null),
		onDelete: caps.delete ? () => openDelete() : undefined,
	}
}
